import type { BillingOffer } from '~/gql/billing/billing'
import { useQuery } from '@apollo/client/react'
import { BillingPaymentTerm } from '@zeus/index'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { GQL_BILLING_OFFERS } from '~/gql/billing/billing'
import { useAuth } from '~/helpers/store/auth'
import { tourAnchor } from '~/helpers/tour/anchors'
import PaymentTermSwitch from '~/pages/backoffice/admin/billing/PaymentTermSwitch'
import TierOfferCard from '~/pages/backoffice/admin/billing/TierOfferCard'
import { useStartCheckout } from '~/pages/backoffice/admin/billing/use-start-checkout'

/**
 * La souscription d'un palier (#352) : les quatre offres au prix de l'échéance
 * choisie, payées sur Stripe Checkout. Les prix viennent de l'API, jamais d'ici.
 * Un dépassement de capacité reste signalé par l'estimation, jamais bloquant.
 */
function TierOffers() {
  const { t } = useTranslation()
  const [term, setTerm] = useState<BillingPaymentTerm>(BillingPaymentTerm.MONTHLY)
  const { data } = useQuery(GQL_BILLING_OFFERS, { fetchPolicy: 'cache-first' })
  const { start, pendingTier } = useStartCheckout()
  // Payer engage l'organisation : pas au nom d'un autre (l'API refuse aussi).
  const impersonating = useAuth(state => Boolean(state.payload?.impersonatorId))
  const offers = (data?.billingOffers ?? []) as BillingOffer[]

  return (
    <Card {...tourAnchor('billing.plan')}>
      <CardHeader>
        <CardTitle>{t('billing.offers.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <PaymentTermSwitch value={term} onChange={setTerm} />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" {...tourAnchor('billing.subscribe')}>
          {offers.map(offer => (
            <TierOfferCard
              key={offer.tier}
              offer={offer}
              term={term}
              pending={pendingTier === offer.tier}
              disabled={pendingTier !== null || impersonating}
              onSubscribe={() => void start(offer.tier, term)}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{t(impersonating ? 'billing.offers.error.impersonation' : 'billing.offers.note')}</p>
      </CardContent>
    </Card>
  )
}

export default TierOffers
